/* eslint-disable no-continue */
/* eslint-disable no-await-in-loop */
import { Box, styled, Typography } from '@mui/material';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  CaseAttachment,
  CaseNote,
  DraftNoteAttachment,
  EconsultUserRoles,
  FileDescription,
  FileWithProgress,
  NoteStatusCodes
} from '@oh-vcp/components-common';
import { useNotifierContext } from '@oh-vcp/components-ui';
import { useTranslation } from 'react-i18next';
import EconsultAttachmentFileItem from '../econsultAttachmentFileItem/EconsultAttachmentFileItem';
import CaseService from '../services/caseService';
import { useEconsultCaseDetailsContext } from '../econsultCaseDetailsContext/EconsultCaseDetailsContext';
import useFileService from '../services/useFileService';

const DragAndDropArea = styled(Box)(({ theme }) => ({
  border: '2px dashed',
  borderColor: theme.palette.base.grey5,
  padding: '40px',
  textAlign: 'center',
  backgroundColor: '#ffffff',
  minHeight: 100
}));

export interface EconsultDragAndDropRef {
  attachFile: () => void;
  getDraftAttachments: () => FileWithProgress[];
  uploadFileList: (fileList: FileList) => void;
}

interface EconsultDragAndDropProps {
  onStartFileUpload?: () => void;
  onCompleteFileUpload?: () => void;
  draftUploadedFiles?: FileWithProgress[];
  onLastNoteNotExists?: () => Promise<CaseNote | undefined>;
  allowDelete?: boolean;
  onFileDeleted?: () => void;
}

const EconsultDragAndDrop = forwardRef<EconsultDragAndDropRef, EconsultDragAndDropProps>(
  (props, ref) => {
    const {
      onStartFileUpload,
      onCompleteFileUpload,
      draftUploadedFiles,
      onLastNoteNotExists,
      allowDelete,
      onFileDeleted
    } = props;
    const [caseAttachments, setCaseAttachments] = useState<DraftNoteAttachment[]>([]);
    const fileDescriptionsRef = useRef<FileDescription[]>([]);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [uploadedFiles, setUploadedFiles] = useState<FileWithProgress[]>([]);
    const { addNotification, removeAllErrorNotifications } = useNotifierContext();
    const { caseDetails, userRole, setFileUploading, abortControllers, cancelUploads } =
      useEconsultCaseDetailsContext();
    const { fetchById } = useFileService(undefined);
    const { t } = useTranslation();
    const maxSizeInBytes = 500 * 1024 * 1024;

    useImperativeHandle(ref, () => ({
      attachFile() {
        if (fileInputRef && fileInputRef.current) {
          fileInputRef.current.click();
        }
      },
      getDraftAttachments() {
        return uploadedFiles;
      },
      uploadFileList(fileList: FileList) {
        processFileList(fileList);
      }
    }));

    useEffect(() => {
      const draftNote = caseDetails?.notes?.find((n) => n.statusCd === NoteStatusCodes.draft);
      if (draftNote && draftNote.attachments && draftNote.attachments.length > 0) {
        setUploadedFiles(
          draftNote.attachments.map((f) => {
            return {
              file: {
                name: f.uploadedFile?.fileName
              } as unknown as File,
              progress: 100,
              uploadedFile: f.uploadedFile,
              fileDescription: f.fileDescription,
              fileId: f.fileId,
              uid: crypto.randomUUID()
            };
          })
        );
      }
    }, [caseDetails]);

    useEffect(() => {
      if (cancelUploads.current) {
        cancelUploads.current = false;
      }
      setCaseAttachments((prevAttachments) => {
        const existingFileIds = new Set(prevAttachments.map((a) => a.fileId));

        const newAttachments =
          caseDetails!
            .notes![0]?.attachments?.map((d) => ({
              fileId: d.uploadedFile?.fileId || 0,
              statusCd: NoteStatusCodes.active,
              fileName: d.uploadedFile?.fileName || '',
              ...(d.attachmentId ? { attachmentId: d.attachmentId } : {}),
              ...(d.fileDescription ? { fileDescription: d.fileDescription } : {})
            }))
            .filter((a) => !existingFileIds.has(a.fileId)) || [];

        return [...prevAttachments, ...newAttachments];
      });
    }, [caseDetails]);

    useEffect(() => {
      if (draftUploadedFiles && draftUploadedFiles.length > 0) {
        setUploadedFiles(draftUploadedFiles);
      }
    }, [draftUploadedFiles]);

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };

    const deleteFile = (fileName: string) => {
      setUploadedFiles((prevFiles) => prevFiles.filter((f) => f.file.name !== fileName));
      if (fileInputRef && fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };

    const deleteFileByUid = (uid: string) => {
      setUploadedFiles((prevFiles) => prevFiles.filter((f) => f.uid !== uid));
      if (fileInputRef && fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    };

    const handleUploadCompleted = () => {
      setFileUploading(false);
      if (onCompleteFileUpload) {
        const incompleteFile = uploadedFiles.find((u) => u.progress < 100);
        if (!incompleteFile) {
          onCompleteFileUpload();
        }
      }
    };

    const updateLastNoteAttachments = async (updatedAttachment: CaseAttachment | undefined) => {
      if (!updatedAttachment) return;
      let userRoleType = userRole || EconsultUserRoles.referrer;
      if (userRole === EconsultUserRoles.referrerDelegate) {
        userRoleType = EconsultUserRoles.referrer;
      } else if (userRole === EconsultUserRoles.consultantDelegate) {
        userRoleType = EconsultUserRoles.consultant;
      }
      let lastNote = caseDetails?.notes?.find(
        (n) =>
          n.statusCd === NoteStatusCodes.draft &&
          n.noteTypeCd &&
          n.noteTypeCd.toLowerCase() === userRoleType?.toLowerCase()
      );
      if (!lastNote) {
        if (!onLastNoteNotExists) return;
        lastNote = await onLastNoteNotExists();
      }
      const updatedNote = {
        ...lastNote,
        attachments: [
          {
            fileId: updatedAttachment.fileId,
            statusCd: updatedAttachment.statusCd,
            ...(updatedAttachment.attachmentId
              ? { attachmentId: updatedAttachment.attachmentId }
              : {}),
            ...(updatedAttachment.fileDescription
              ? { fileDescription: updatedAttachment.fileDescription }
              : {})
          }
        ]
      };
      const upsertedLastNote = await CaseService.upsertCaseNote(updatedNote);
      if (upsertedLastNote?.noteId && upsertedLastNote?.attachments) {
        setCaseAttachments(
          upsertedLastNote.attachments.map((a) => ({
            fileId: a.fileId || 0,
            attachmentId: a.attachmentId,
            fileName: a.uploadedFile?.fileName || '',
            statusCd: a.statusCd || '',
            fileDescription: a.fileDescription
          }))
        );
      }
    };

    const handleAddNoteAttachment = async (
      fileId: number,
      fileName: string,
      status: string,
      fileDescription: string | undefined
    ) => {
      removeAllErrorNotifications();
      if (status === 'VIRUS_FOUND') {
        addNotification({
          type: 'error',
          message: t('Case.draft.fileVirusFound').replace('FILE_NAME', fileName)
        });
        return;
      }
      const updatedAttachment = {
        fileId,
        statusCd: NoteStatusCodes.active,
        fileName,
        ...(fileDescription ? { fileDescription } : {})
      };
      try {
        await updateLastNoteAttachments(updatedAttachment);
        addNotification({
          type: 'success',
          message: t('Case.draft.fileHasBeenUploaded')
        });
      } catch (error) {
        addNotification({
          type: 'error',
          message: t('Case.draft.errorUploadingAttachment'),
          details: JSON.stringify(error)
        });
      }
    };

    const handleAttachmentDeleted = async (fileId: string | undefined) => {
      const attachmentToDelete = caseAttachments.find((f) => f.fileId?.toString() === fileId);
      if (!attachmentToDelete || !attachmentToDelete.attachmentId) return;
      attachmentToDelete.statusCd = NoteStatusCodes.deleted;
      removeAllErrorNotifications();
      await updateLastNoteAttachments(attachmentToDelete);
      if (onFileDeleted) {
        onFileDeleted();
      }
      addNotification({
        type: 'success',
        message: t('Case.draft.fileDeletedSuccessfully')
      });
    };

    const handleFileUpload = async (files: FileWithProgress[]) => {
      for (let i = 0; i < files.length; i += 1) {
        if (cancelUploads.current) {
          cancelUploads.current = false;
          return;
        }
        setFileUploading(true);
        if (onStartFileUpload) {
          onStartFileUpload();
        }
        const controller = new AbortController();
        const file = files[i];
        file.uid = crypto.randomUUID();
        abortControllers.current.set(file.uid, controller);
        removeAllErrorNotifications();
        try {
          const fileInfo = await CaseService.uploadFile(
            file.file,
            (progress: number) => {
              setUploadedFiles((prevFiles) =>
                prevFiles.map((prevFile) =>
                  prevFile.uid === file.uid
                    ? { ...prevFile, progress: Math.min(progress, 90) }
                    : prevFile
                )
              );
            },
            controller.signal
          );

          abortControllers.current.delete(file.uid);
          if (fileInfo) {
            if (fileInfo.status === 'VIRUS_FOUND') {
              deleteFile(fileInfo.fileName);
              handleUploadCompleted();
              continue;
            }
            if (fileInfo.status === 'SCANNING') {
              let fileStatus = fileInfo.status;
              let count = 0;
              while (fileStatus === 'SCANNING' && count <= 5) {
                await new Promise((resolve) => {
                  setTimeout(resolve, 5000);
                });
                const fileDetails = await fetchById(fileInfo.id);
                fileStatus = fileDetails?.status;
                count += 1;
              }
              if (fileStatus === 'NOT_SCANNED' || fileStatus === 'SCANNING') {
                setUploadedFiles((prevFiles) =>
                  prevFiles.map((prevFile) =>
                    prevFile.uid === file.uid
                      ? { ...prevFile, progress: 100, uploadFailed: true }
                      : prevFile
                  )
                );
                addNotification({
                  type: 'error',
                  message: t('Case.file.scannerNotAvailable')
                });
                handleUploadCompleted();
                continue;
              }
            }
            setUploadedFiles((prevFiles) =>
              prevFiles.map((prevFile) =>
                prevFile.uid === file.uid ? { ...prevFile, fileId: fileInfo.id } : prevFile
              )
            );
            const existingDescription = fileDescriptionsRef.current?.find(
              (u) => u.uid === file.uid
            );
            await handleAddNoteAttachment(
              fileInfo.id,
              fileInfo.fileName,
              fileInfo.status,
              existingDescription?.description
            );
            setUploadedFiles((prevFiles) =>
              prevFiles.map((prevFile) =>
                prevFile.uid === file.uid ? { ...prevFile, progress: 100 } : prevFile
              )
            );
          }
        } catch (err: any) {
          if (err?.response?.data) {
            const errDetails: string = err.response.data.details;
            let message = errDetails;
            if (errDetails.toLowerCase().indexOf('virus') > -1) {
              message = t('Case.draft.fileVirusFound').replace('FILE_NAME', file.file.name);
              if (file.uid) {
                deleteFileByUid(file.uid);
              }
            } else if (errDetails.toLocaleLowerCase().indexOf('file extension') > -1) {
              message = `${t('Case.file.unsupportedMessage1')} ${t(
                'Case.file.unsupportedMessage2'
              )}`;
            } else {
              message = errDetails;
            }
            setUploadedFiles((prevFiles) =>
              prevFiles.map((prevFile) =>
                prevFile.uid === file.uid
                  ? { ...prevFile, progress: 100, uploadFailed: true }
                  : prevFile
              )
            );
            addNotification({
              type: 'error',
              message
            });
          }
        }
      }
      handleUploadCompleted();
    };

    const validExtensions = [
      '.3gpp',
      '.3gpp2',
      '.3gp',
      '.avi',
      '.flv',
      '.m4a',
      '.mkv',
      '.mov',
      '.mp3',
      '.mp4',
      '.mpg',
      '.mpeg',
      '.ogg',
      '.qt',
      '.wav',
      '.wma',
      '.wmv',
      '.doc',
      '.docx',
      '.pdf',
      '.ppt',
      '.pptx',
      '.pps',
      '.ppsx',
      '.rtf',
      '.txt',
      '.xls',
      '.xlsx',
      '.csv',
      '.xps',
      '.bmp',
      '.dcm',
      '.exif',
      '.gif',
      '.jpg',
      '.jpeg',
      '.jfif',
      '.png',
      '.raw',
      '.tiff',
      '.sia',
      '.tif',
      '.zip'
    ];

    const getValidFiles = (files: FileWithProgress[]) => {
      const validFiles: FileWithProgress[] = [];
      removeAllErrorNotifications();
      files.forEach((file) => {
        if (validExtensions.some((ext) => file.file.name.toLowerCase().endsWith(ext))) {
          if (file.file.size > maxSizeInBytes) {
            addNotification({
              type: 'error',
              message: `${t('Case.file.unsupportedMessage3')} ${file.file.name} ${t(
                'Case.file.unsupportedMessage4'
              )}`
            });
            return;
          }
          validFiles.push(file);
        } else {
          addNotification({
            type: 'error',
            message: `${t('Case.file.unsupportedMessage1')} ${file.file.name} ${t(
              'Case.file.unsupportedMessage2'
            )}`
          });
        }
      });

      return validFiles;
    };

    const processFileList = async (fileList: FileList) => {
      const files: FileWithProgress[] = Array.from(fileList).map((file) => ({
        file: new File([file], file.name.replace(/,/g, ''), {
          type: file.type
        }),
        progress: 0
      }));
      const validFiles: FileWithProgress[] = getValidFiles(files);
      setUploadedFiles((prevFiles) => [...prevFiles, ...validFiles]);
      await handleFileUpload(validFiles);
    };

    const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      await processFileList(event.dataTransfer.files);
    };

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files) {
        await processFileList(event.target.files);
      }
    };

    const handleFileDelete = async (uid: string | undefined, fileId: string | undefined) => {
      if (uid) {
        const controller = abortControllers.current.get(uid);
        if (controller) {
          controller.abort();
          abortControllers.current.delete(uid);
        }
      }
      setUploadedFiles((prevFiles) => prevFiles.filter((f) => f.uid !== uid));
      if (fileInputRef && fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      if (fileDescriptionsRef.current) {
        fileDescriptionsRef.current = fileDescriptionsRef.current.filter((u) => u.uid !== uid);
      }
      await handleAttachmentDeleted(fileId);
    };

    const handleFileDescriptionChanged = async (
      uid: string | undefined,
      fileDescription: string,
      fileId: string | number | undefined
    ) => {
      if (fileId) {
        const updatedAttachment = caseAttachments.find(
          (a) => a.fileId.toString() === fileId.toString()
        );
        if (!updatedAttachment) return;
        updatedAttachment.fileDescription = fileDescription;
        await updateLastNoteAttachments(updatedAttachment);
      }
      const prev = fileDescriptionsRef.current;
      const existingIndex = prev.findIndex((item) => item.uid === uid);
      if (existingIndex !== -1) {
        prev[existingIndex] = { uid, description: fileDescription };
      } else {
        prev.push({ uid, description: fileDescription });
      }
    };

    return (
      <DragAndDropArea onDragOver={handleDragOver} onDrop={handleDrop}>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          style={{
            display: 'none'
          }}
          id="fileUpload"
          // eslint-disable-next-line max-len
          accept={validExtensions.join(',')}
        />
        {uploadedFiles
          .sort((a, b) => {
            if (!a.fileId && !b.fileId) return 0;
            if (!a.fileId) return 1;
            if (!b.fileId) return -1;
            return parseInt(a.fileId.toString(), 10) - parseInt(b.fileId.toString(), 10);
          })
          .map((file, index) => (
            <EconsultAttachmentFileItem
              key={index}
              file={file}
              onDelete={handleFileDelete}
              onDescriptionChanged={handleFileDescriptionChanged}
              disableDelete={Boolean(
                caseDetails?.originalCaseId && file.uploadedFile && !allowDelete
              )}
            />
          ))}
        <Typography sx={{ fontSize: 20, color: 'base.grey4' }}>Drag and drop files here</Typography>
        {uploadedFiles && uploadedFiles.length > 0 && (
          <Typography variant="body4" sx={{ color: 'base.grey4' }}>
            Please ensure that the recipient has appropriate viewers for the attached files.
          </Typography>
        )}
      </DragAndDropArea>
    );
  }
);

export default EconsultDragAndDrop;
